import { create } from "zustand";
import type { ManifestLayer } from "../types/contract";

// Covariates the user chose to "view on map" from the Field data panel. They ride the SAME
// map pipeline as the manifest layers (MapWindow merges these in) so rendering / sidebar controls /
// Legend all work unchanged. Keyed by field id; each entry is a resolved ManifestLayer
// (id `cov-<key>`). Client-only UI state — the covariate data itself comes from the backend.
interface ViewedState {
  byField: Record<string, ManifestLayer[]>;
  add: (fieldId: string, layer: ManifestLayer) => void;
  remove: (fieldId: string, layerId: string) => void;
  clear: (fieldId: string) => void;
}

export const useViewedCovariates = create<ViewedState>((set) => ({
  byField: {},
  add: (fieldId, layer) =>
    set((s) => {
      const cur = s.byField[fieldId] ?? [];
      if (cur.some((l) => l.id === layer.id)) return s; // already viewing — no-op
      // The user EXPLICITLY asked to see this covariate — force it visible with a usable
      // opacity, whatever the backend row's display defaults say (live CV1b rows come from
      // v_field_layers where covariates are default-off; that made "View" a silent no-op).
      const visible: ManifestLayer = {
        ...layer,
        default_visible: true,
        default_opacity: layer.default_opacity || 0.85,
      };
      return { byField: { ...s.byField, [fieldId]: [...cur, visible] } };
    }),
  remove: (fieldId, layerId) =>
    set((s) => ({
      byField: {
        ...s.byField,
        [fieldId]: (s.byField[fieldId] ?? []).filter((l) => l.id !== layerId),
      },
    })),
  clear: (fieldId) =>
    set((s) => ({ byField: { ...s.byField, [fieldId]: [] } })),
}));
